import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    CategoryPills,
    type CategoryPillsProps,
} from '~/ui-rocco/pages/home-page/components/CategoryPills';

/** The distinct rows a wrapping run of pills sits on, top to bottom. */
const rowTopsOf = (pills: HTMLElement[]) =>
    pills.reduce<number[]>(
        (tops, pill) =>
            tops.includes(pill.offsetTop) ? tops : [...tops, pill.offsetTop],
        [],
    );

/**
 * Outline pills held to a number of lines: past them, the last pill says how
 * many more there are, and pressing it shows them all. Measured as laid out,
 * so the cut follows the column's width and the pills' own lengths.
 */
export const ClampedTagPills = ({
    items,
    maxLines,
    ...pillProps
}: CategoryPillsProps & { maxLines: number }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [shown, setShown] = useState(items.length);

    // A different tag list is laid out from scratch, so the cut is found again
    // from the full list rather than walked down from the old one's.
    const signature = items.map(({ label }) => label).join('\n');
    const [shownFor, setShownFor] = useState(signature);
    if (shownFor !== signature) {
        setShownFor(signature);
        setShown(items.length);
    }

    // A new width lays the pills out afresh, so the cut is found again.
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        let width = root.clientWidth;
        const observer = new ResizeObserver(() => {
            if (root.clientWidth === width) return;
            width = root.clientWidth;
            setShown(items.length);
        });
        observer.observe(root);
        return () => observer.disconnect();
    }, [items.length]);

    // Too many lines: first cut to the pills on the allowed lines, then, while
    // the "more" pill itself spills onto another, give up one pill at a time.
    useLayoutEffect(() => {
        if (expanded) return;
        const wrap = rootRef.current?.firstElementChild?.lastElementChild;
        if (!wrap) return;
        const pills = Array.from(wrap.children) as HTMLElement[];
        const tops = rowTopsOf(pills);
        const lastTop = tops[maxLines - 1];
        if (tops.length <= maxLines || lastTop === undefined) return;
        if (shown === items.length) {
            const firstOver = pills.findIndex(
                (pill) => pill.offsetTop > lastTop,
            );
            setShown(Math.max(0, firstOver));
            return;
        }
        setShown((count) => Math.max(0, count - 1));
    }, [expanded, shown, items.length, maxLines]);

    const hidden = items.length - shown;
    const visible =
        expanded || hidden <= 0
            ? items
            : [
                  ...items.slice(0, shown),
                  {
                      label: `+${hidden} More`,
                      onClick: () => setExpanded(true),
                  },
              ];
    return (
        <div ref={rootRef}>
            <CategoryPills items={visible} {...pillProps} />
        </div>
    );
};
